import { createColumnHelper } from '@tanstack/react-table';
import { useCallback, useContext } from 'react';
import { IconButton } from 'src/components/Buttons';
import { Content } from 'src/contexts/ContentContext';
import { ModalContext } from 'src/contexts/ModalContext';
import { useSellerAccountContext } from 'src/contexts/SellerAccountContext';
import { Stack } from 'src/core/ui';
import { TableCellDiv } from 'src/core/ui/TableCellDiv';
import { EditIcon } from 'src/svgs/Viagogo';
import { ContentId } from 'src/utils/constants/contentId';
import {
  EVENT_NOTIFICATION_STATUS_TO_CID,
  EVENT_NOTIFICATION_TYPE_TO_CID,
} from 'src/utils/constants/contentIdMaps';
import {
  EventNotification,
  EventNotificationStatus,
  EventNotificationType,
} from 'src/WebApiController';

import { NotificationModal } from './NotificationModal';

const columnHelper = createColumnHelper<EventNotification | null>();

export const NOTIFICATIONS_TABLE_COLUMNS_CONFIG = [
  columnHelper.accessor('name', {
    id: 'name',
    header: () => (
      <TableCellDiv align="left">
        <Content id={ContentId.Name} />
      </TableCellDiv>
    ),
    cell: function Cell({ getValue }) {
      return <TableCellDiv align="left">{getValue()}</TableCellDiv>;
    },
  }),
  columnHelper.accessor((data) => data?.notificationType, {
    id: 'type',
    header: () => (
      <TableCellDiv align="left">
        <Content id={ContentId.Type} />
      </TableCellDiv>
    ),
    cell: function Cell({ getValue }) {
      const type = getValue() as EventNotificationType;
      if (!type) {
        return;
      }

      return (
        <TableCellDiv align="left">
          <span style={{ textTransform: 'none' }}>
            <Content
              id={
                EVENT_NOTIFICATION_TYPE_TO_CID[
                  getValue() as EventNotificationType
                ]
              }
            />
          </span>
        </TableCellDiv>
      );
    },
  }),
  columnHelper.accessor((data) => data, {
    id: 'destination',
    header: () => (
      <TableCellDiv align="left">
        <Content id={ContentId.Destination} />
      </TableCellDiv>
    ),
    cell: function Cell({ getValue }) {
      const { getUserInfo } = useSellerAccountContext();
      const data = getValue<EventNotification>();
      const destination =
        data.notificationType === EventNotificationType.Email
          ? data.userIdTargets
              .map((uid) => getUserInfo(uid)?.email || '')
              .join(', ')
          : data.webhookTargets.map((webhook) => webhook.url).join(', ');
      return (
        <TableCellDiv align="left">
          <span style={{ textTransform: 'none' }}>{destination}</span>
        </TableCellDiv>
      );
    },
  }),
  columnHelper.accessor('status', {
    id: 'status',
    header: () => (
      <TableCellDiv align="left">
        <Content id={ContentId.Status} />
      </TableCellDiv>
    ),
    cell: function Cell({ getValue }) {
      const type = getValue() as EventNotificationStatus;
      if (!type) {
        return;
      }

      return (
        <TableCellDiv align="left">
          <span style={{ textTransform: 'none' }}>
            <Content
              id={
                EVENT_NOTIFICATION_STATUS_TO_CID[
                  getValue() as EventNotificationStatus
                ]
              }
            />
          </span>
        </TableCellDiv>
      );
    },
  }),
  columnHelper.accessor('filters', {
    id: 'filter',
    header: () => (
      <TableCellDiv align="left">
        <Content id={ContentId.Filter} />
      </TableCellDiv>
    ),
    cell: function Cell({ getValue }) {
      return (
        <TableCellDiv align="left">{JSON.stringify(getValue())}</TableCellDiv>
      );
    },
  }),
  columnHelper.accessor((data) => data, {
    id: 'actions',
    header: () => (
      <TableCellDiv align="right">
        <Content id={ContentId.Actions} />
      </TableCellDiv>
    ),
    enableSorting: false,
    size: 40,

    cell: function Cell({ getValue }) {
      const { setModal, closeModal } = useContext(ModalContext);
      const data = getValue<EventNotification>();

      const onEditClick = useCallback(() => {
        setModal({
          children: (
            <NotificationModal
              isOpen={true}
              notificationInfo={data}
              onClose={closeModal}
            />
          ),
          clickOutsideToClose: true,
          centered: true,
          size: 'sm',
        });
      }, [closeModal, data, setModal]);

      return (
        <TableCellDiv align="right" showTooltip={false}>
          <Stack>
            <IconButton
              icon={<EditIcon withHoverEffect />}
              onClick={onEditClick}
            />
          </Stack>
        </TableCellDiv>
      );
    },
  }),
];
